import storage from './storage';
import { AI_PROVIDERS, DEFAULT_AI_PROVIDER } from '../constants/aiProviders';

const CONFIG_KEY = 'crm_ai_config';
const LEGACY_CLAUDE_KEY = 'crm_claude_api_key';

const DEFAULT_CONFIG = {
  provider: DEFAULT_AI_PROVIDER,
  apiKey: '',
  model: '',
};

function envOverride() {
  const apiKey = (process.env.REACT_APP_AI_API_KEY || '').trim();
  if (!apiKey) return null;
  const provider = (process.env.REACT_APP_AI_PROVIDER || DEFAULT_AI_PROVIDER).trim();
  const model = (process.env.REACT_APP_AI_MODEL || '').trim();
  return {
    provider: AI_PROVIDERS[provider] ? provider : DEFAULT_AI_PROVIDER,
    apiKey,
    model,
    source: 'env',
  };
}

function migrateLegacyKey() {
  const legacy = storage.get(LEGACY_CLAUDE_KEY, '');
  if (!legacy || typeof legacy !== 'string' || !legacy.trim()) return null;
  const cfg = { provider: 'anthropic', apiKey: legacy.trim(), model: '' };
  storage.set(CONFIG_KEY, cfg);
  try { localStorage.removeItem(LEGACY_CLAUDE_KEY); } catch { /* ignore */ }
  return cfg;
}

/** @returns {{ provider: string, apiKey: string, model: string, source?: string }} */
export function getAiConfig() {
  const fromEnv = envOverride();
  if (fromEnv) return { ...DEFAULT_CONFIG, ...fromEnv };

  let cfg = storage.get(CONFIG_KEY, null);
  if (!cfg?.apiKey) {
    const migrated = migrateLegacyKey();
    if (migrated) cfg = migrated;
  }
  if (!cfg || typeof cfg !== 'object') return { ...DEFAULT_CONFIG };

  const provider = AI_PROVIDERS[cfg.provider] ? cfg.provider : DEFAULT_AI_PROVIDER;
  return {
    provider,
    apiKey: String(cfg.apiKey || '').trim(),
    model: String(cfg.model || '').trim(),
    source: 'local',
  };
}

export function setAiConfig(partial) {
  const current = getAiConfig();
  if (current.source === 'env') return current;

  const next = {
    provider: partial.provider != null ? partial.provider : current.provider,
    apiKey: partial.apiKey != null ? String(partial.apiKey).trim() : current.apiKey,
    model: partial.model != null ? String(partial.model).trim() : current.model,
  };
  if (!AI_PROVIDERS[next.provider]) next.provider = DEFAULT_AI_PROVIDER;
  storage.set(CONFIG_KEY, next);
  return next;
}

export function isAiConfigured() {
  return Boolean(getAiConfig().apiKey);
}

export function isAiEnvLocked() {
  return getAiConfig().source === 'env';
}

export function getActiveProvider() {
  const { provider } = getAiConfig();
  return AI_PROVIDERS[provider] || AI_PROVIDERS[DEFAULT_AI_PROVIDER];
}

function resolveModel(tier = 'fast') {
  const cfg = getAiConfig();
  if (cfg.model) return cfg.model;
  const meta = getActiveProvider();
  return tier === 'fast' ? meta.fastModel : meta.fastModel;
}

function extractJson(text) {
  const jsonMatch = String(text || '').match(/\{[\s\S]*\}/);
  if (!jsonMatch) throw new Error('Keine JSON-Antwort von der KI');
  return JSON.parse(jsonMatch[0]);
}

/** API-Fehler für Nutzer verständlich machen (DE) */
export function formatAiError(message, provider) {
  const m = String(message || '');
  const lower = m.toLowerCase();
  if (lower.includes('quota') || lower.includes('exceeded') || lower.includes('billing')) {
    if (provider === 'openai') {
      return 'OpenAI-Kontingent aufgebraucht oder kein Guthaben. Billing unter platform.openai.com prüfen – oder Anbieter „Google Gemini“ wählen (Free Tier).';
    }
    if (provider === 'gemini') {
      return 'Gemini-Limit erreicht. Kurz warten oder Modell/Quota in Google AI Studio prüfen.';
    }
    return 'API-Kontingent aufgebraucht. Billing beim Anbieter prüfen oder zu Google Gemini wechseln.';
  }
  if (lower.includes('invalid api key') || lower.includes('api key not valid')) {
    return 'Ungültiger API-Key. Bitte in den Einstellungen prüfen und neu eintragen.';
  }
  return m;
}

async function parseApiError(response, provider, fallbackLabel) {
  const err = await response.json().catch(() => ({}));
  const raw = err?.error?.message || err?.message || `${fallbackLabel} ${response.status}`;
  throw new Error(formatAiError(raw, provider));
}

async function completeAnthropic({ prompt, system, maxTokens, model }) {
  const { apiKey } = getAiConfig();
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model,
      max_tokens: maxTokens,
      system: system || undefined,
      messages: [{ role: 'user', content: prompt }],
    }),
  });

  if (!response.ok) await parseApiError(response, 'anthropic', 'Anthropic API Fehler');

  const data = await response.json();
  return data.content?.[0]?.text || '';
}

async function completeOpenAI({ prompt, system, maxTokens, model, jsonMode }) {
  const { apiKey } = getAiConfig();
  const messages = [];
  if (system) messages.push({ role: 'system', content: system });
  messages.push({ role: 'user', content: prompt });

  const body = {
    model,
    max_tokens: maxTokens,
    messages,
  };
  if (jsonMode) body.response_format = { type: 'json_object' };

  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) await parseApiError(response, 'openai', 'OpenAI API Fehler');

  const data = await response.json();
  return data.choices?.[0]?.message?.content || '';
}

async function completeGemini({ prompt, system, maxTokens, model, jsonMode }) {
  const { apiKey } = getAiConfig();
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`;

  const generationConfig = {
    maxOutputTokens: maxTokens,
    temperature: 0.35,
  };
  if (jsonMode) generationConfig.responseMimeType = 'application/json';

  const body = {
    contents: [{ role: 'user', parts: [{ text: prompt }] }],
    generationConfig,
  };
  if (system) {
    body.systemInstruction = { parts: [{ text: system }] };
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) await parseApiError(response, 'gemini', 'Gemini API Fehler');

  const data = await response.json();
  const candidate = data.candidates?.[0];
  const text = candidate?.content?.parts?.map(p => p.text).filter(Boolean).join('') || '';
  if (!text && candidate?.finishReason === 'SAFETY') {
    throw new Error('Gemini hat die Antwort aus Sicherheitsgründen blockiert.');
  }
  return text;
}

/**
 * Zentrale KI-Anfrage – Basis für alle CRM-KI-Features.
 * @param {{ prompt: string, system?: string, maxTokens?: number, model?: string, tier?: 'fast'|'default' }}
 */
export async function aiComplete({ prompt, system, maxTokens = 512, model, tier = 'fast' }) {
  if (!isAiConfigured()) {
    throw new Error('Kein KI API Key konfiguriert. Bitte unter Einstellungen → Integrationen hinterlegen.');
  }

  const cfg = getAiConfig();
  const resolvedModel = model || resolveModel(tier);
  const opts = { prompt, system, maxTokens, model: resolvedModel };

  if (cfg.provider === 'openai') return completeOpenAI(opts);
  if (cfg.provider === 'gemini') return completeGemini({ ...opts, jsonMode: false });
  return completeAnthropic(opts);
}

/**
 * KI-Anfrage mit JSON-Antwort (strukturierte Tasks).
 */
export async function aiCompleteJson({ prompt, system, maxTokens = 256, model, tier = 'fast' }) {
  const cfg = getAiConfig();
  const jsonHint = system
    ? `${system}\n\nAntworte ausschließlich mit gültigem JSON, ohne Markdown.`
    : 'Antworte ausschließlich mit gültigem JSON, ohne Markdown oder Erklärung.';

  const resolvedModel = model || resolveModel(tier);
  let text;

  if (cfg.provider === 'gemini') {
    text = await completeGemini({
      prompt,
      system: jsonHint,
      maxTokens,
      model: resolvedModel,
      jsonMode: true,
    });
  } else if (cfg.provider === 'openai') {
    text = await completeOpenAI({
      prompt,
      system: jsonHint,
      maxTokens,
      model: resolvedModel,
      jsonMode: true,
    });
  } else {
    text = await completeAnthropic({
      prompt,
      system: jsonHint,
      maxTokens,
      model: resolvedModel,
    });
  }

  try {
    if (cfg.provider === 'openai' || cfg.provider === 'gemini') {
      return JSON.parse(text.trim());
    }
    return extractJson(text);
  } catch {
    return extractJson(text);
  }
}

// ── Abwärtskompatibilität (ältere Imports) ──────────────────────────────────
export function getClaudeApiKey() {
  return getAiConfig().apiKey;
}
export function setClaudeApiKey(key) {
  return setAiConfig({ apiKey: key, provider: 'anthropic' });
}
export function hasClaudeApiKey() {
  return isAiConfigured();
}
